/*#
# Guidance: the way in

An agent needs a way into the Library (board #2497): by topic when it knows
the area, and one entry point when it knows nothing.

- **The root:** one entry tagged `index:root`, the CLAUDE.md / llms.txt of
  the Library. `virta guide` shows it with every topic's index, so any
  topic is one hop away. It's also the MCP resource `virta://guidance` and
  `registry/llms.txt` in the export, and `virta brief` points to it.
- **Topic indexes:** an entry tagged `index:topic` and `topic:<name>` is
  that topic's start page. `virta guide <topic>` shows it, then the
  topic's entries.
- **Scope:** an entry applies `scope:global`, `scope:topic` (within its
  topics; the default for an entry with topics), or `scope:<project>` (one
  project). For a project, the narrower scope comes first: project, then
  topic, then global — a project's CLAUDE.md beats the practices, which
  beat model priors, made mechanical.
- **Overrides are visible:** a narrower entry that conflicts with a
  default says so, `overrides:#n`, and the default is listed as overridden
  by it for that project, never silently contradicted.

```
virta guide                      # the root, and every topic's index
virta guide coding               # one topic, in precedence order for this repo
virta guide coding --project tosijs-ui
```
*/

/*{ "parent": "Reference", "order": 39 }*/

import type { Task } from './model'
import { isEntry } from './practices'
import type { VirtaStore } from './store'
import { tagValues, TYPE_KEY } from './tags'

export const INDEX_KEY = 'index'
export const SCOPE_KEY = 'scope'
export const OVERRIDES_KEY = 'overrides'

/** Where an entry applies. */
export type GuideScope =
  | { kind: 'global' }
  | { kind: 'topic' }
  | { kind: 'project'; project: string }

/** An entry's scope: its `scope:` tag, else topic when it has topics, else global. */
export function scopeOf(t: Pick<Task, 'tags'>): GuideScope {
  const v = tagValues(t.tags, SCOPE_KEY)[0] ?? ''
  if (v === 'global') return { kind: 'global' }
  if (v === 'topic') return { kind: 'topic' }
  if (v) return { kind: 'project', project: v }
  return tagValues(t.tags, 'topic').length
    ? { kind: 'topic' }
    : { kind: 'global' }
}

const RANK = { project: 0, topic: 1, global: 2 } as const

export interface GuideEntry {
  num: number
  id: string
  title: string
  type: string
  scope: string
  /** the narrower entry that overrides this one for the project, if any */
  overriddenBy: { num: number; title: string } | null
}

export interface Guide {
  project: string
  /** the root entry, if there is one */
  root: Task | null
  /** every topic, with its index entry and how many entries it has */
  topics: { topic: string; index: Task | null; count: number }[]
  /** the topic asked for, its index, and its entries in precedence order */
  topic: string
  index: Task | null
  entries: GuideEntry[]
  /** the same, as markdown: what the CLI prints and the MCP resource serves */
  text: string
}

/** Whether an entry applies to a project: anything but another project's. */
const appliesTo = (t: Task, project: string) => {
  const s = scopeOf(t)
  return s.kind !== 'project' || s.project === project
}

const scopeLabel = (t: Task) => {
  const s = scopeOf(t)
  return s.kind === 'project' ? `project ${s.project}` : s.kind
}

/**
 * The guide (pure: reads the store). Without a topic: the root and every
 * topic's index. With one: that topic's index and its entries for
 * `project`, narrowest scope first, overridden defaults marked.
 */
export function guide(
  store: VirtaStore,
  options: { topic?: string; project?: string } = {}
): Guide {
  const project = options.project ?? ''
  const topic = (options.topic ?? '').trim().toLowerCase()
  const open = store.tasks.filter((t) => isEntry(t) && !t.closedAt)
  const indexOf = (t: Task) => tagValues(t.tags, INDEX_KEY)[0] ?? ''
  const root = open.find((t) => indexOf(t) === 'root') ?? null
  const names = [
    ...new Set(open.flatMap((t) => tagValues(t.tags, 'topic'))),
  ].sort()
  const topics = names.map((name) => ({
    topic: name,
    index:
      open.find(
        (t) =>
          indexOf(t) === 'topic' && tagValues(t.tags, 'topic').includes(name)
      ) ?? null,
    count: open.filter(
      (t) =>
        !indexOf(t) &&
        tagValues(t.tags, 'topic').includes(name) &&
        appliesTo(t, project)
    ).length,
  }))
  const chosen = topics.find((t) => t.topic === topic)
  const inTopic = topic
    ? open
        .filter(
          (t) =>
            !indexOf(t) &&
            tagValues(t.tags, 'topic').includes(topic) &&
            appliesTo(t, project)
        )
        .sort(
          (a, b) =>
            RANK[scopeOf(a).kind] - RANK[scopeOf(b).kind] || a.num - b.num
        )
    : []
  // an applicable narrower entry that says it overrides a default
  const overriders = new Map<number, Task>()
  for (const t of inTopic)
    for (const v of tagValues(t.tags, OVERRIDES_KEY)) {
      const n = Number(/^#(\d+)$/.exec(v)?.[1] ?? NaN)
      if (Number.isInteger(n)) overriders.set(n, t)
    }
  const entries: GuideEntry[] = inTopic.map((t) => {
    const by = overriders.get(t.num)
    return {
      num: t.num,
      id: t.id,
      title: t.title,
      type: tagValues(t.tags, TYPE_KEY)[0] ?? '',
      scope: scopeLabel(t),
      overriddenBy:
        by && RANK[scopeOf(by).kind] < RANK[scopeOf(t).kind]
          ? { num: by.num, title: by.title }
          : null,
    }
  })
  const g: Omit<Guide, 'text'> = {
    project,
    root,
    topics,
    topic,
    index: chosen?.index ?? null,
    entries,
  }
  return { ...g, text: guideText(g) }
}

/** The guide as markdown. */
function guideText(g: Omit<Guide, 'text'>): string {
  const lines: string[] = []
  if (!g.topic) {
    if (g.root) lines.push(`# ${g.root.title}`, '', g.root.body.trim(), '')
    else
      lines.push(
        '# Guidance',
        '',
        'No root entry yet: tag one guidance entry `index:root` to make it the way in.',
        ''
      )
    lines.push('## Topics', '')
    if (!g.topics.length) lines.push('(no topics yet)')
    for (const t of g.topics)
      lines.push(
        `- **${t.topic}** — ${
          t.index ? `${t.index.title} (#${t.index.num})` : 'no index yet'
        }, ${t.count} entr${t.count === 1 ? 'y' : 'ies'}: \`virta guide ${
          t.topic
        }\``
      )
    return lines.join('\n') + '\n'
  }
  lines.push(`# ${g.topic}${g.project ? ` (for ${g.project})` : ''}`, '')
  if (g.index)
    lines.push(
      `## ${g.index.title} (#${g.index.num})`,
      '',
      g.index.body.trim(),
      ''
    )
  else if (!g.entries.length) lines.push(`No guidance on ${g.topic} yet.`, '')
  if (g.entries.length) {
    lines.push('## Entries, narrowest scope first', '')
    for (const e of g.entries)
      lines.push(
        `- #${e.num} ${e.title} — ${e.type}, ${e.scope}${
          e.overriddenBy
            ? `; **overridden** here by #${e.overriddenBy.num} ${e.overriddenBy.title}`
            : ''
        }`
      )
  }
  return lines.join('\n') + '\n'
}
